import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Checkbox,
  Button,
  Space,
  Typography,
  Card,
  Tabs,
  Row,
  Col,
  Divider,
  message
} from 'antd';
import { SaveOutlined, CloseOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  CreateTitularDTO,
  QuantidadeTitular,
  FaixaEtaria,
  DadoTitular,
  OrigemDados,
  ConsentimentoTitular,
  FrequenciaColeta,
  Dado,
  Area
} from '../../../types/dashboard/mapeamentos.types';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface TitularFormProps {
  onSubmit: (data: CreateTitularDTO) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<CreateTitularDTO>;
  dados: Dado[];
  areas: Area[];
  loading?: boolean;
}

const TitularForm: React.FC<TitularFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  dados,
  areas,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basico');

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData);
    }
  }, [initialData, form]);

  const handleSubmit = async (values: CreateTitularDTO) => {
    setSubmitting(true);
    try {
      await onSubmit(values);
      message.success('Titular salvo com sucesso!');
    } catch (error) {
      message.error('Erro ao salvar titular');
    } finally {
      setSubmitting(false);
    }
  };

  const addDado = () => {
    const currentDados = form.getFieldValue('dados') || [];
    const novoDado: Partial<DadoTitular> = {
      dado_id: '',
      motivo: '',
      origem: OrigemDados.TITULAR,
      consentimento_titular: ConsentimentoTitular.NAO_SABE,
      frequencia: FrequenciaColeta.CONTINUO,
      compartilhamento: [],
      areas_compartilhadas: [],
      externos: [],
      internacional: false,
      local: []
    };
    
    form.setFieldsValue({
      dados: [...currentDados, novoDado]
    });
  };

  const removeDado = (index: number) => {
    const currentDados = form.getFieldValue('dados') || [];
    form.setFieldsValue({
      dados: currentDados.filter((_: any, i: number) => i !== index)
    });
  };

  return (
    <Card
      title={
        <div className="flex justify-between items-center">
          <Title level={3} style={{ margin: 0 }}>
            {initialData ? 'Editar Titular' : 'Novo Titular'}
          </Title>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCancel}
          />
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          faixa_etaria: [],
          dados: []
        }}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Informações Básicas" key="basico">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Categoria do Titular"
                  name="categoria"
                  rules={[
                    { max: 500, message: 'Categoria deve ter no máximo 500 caracteres' }
                  ]}
                >
                  <Input placeholder="Ex: Clientes, Funcionários, Fornecedores..." />
                </Form.Item>
              </Col>
              
              <Col xs={24} md={12}>
                <Form.Item
                  label="Quantidade de Pessoas"
                  name="quantidade"
                >
                  <Select placeholder="Selecione uma faixa">
                    {Object.values(QuantidadeTitular).map(quantidade => (
                      <Option key={quantidade} value={quantidade}>
                        {quantidade}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Faixa Etária"
              name="faixa_etaria"
            >
              <Checkbox.Group>
                <Row>
                  {Object.values(FaixaEtaria).map(faixa => (
                    <Col xs={24} sm={12} key={faixa}>
                      <Checkbox value={faixa}>{faixa}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </TabPane>

          <TabPane tab="Dados Pessoais" key="dados">
            <div style={{ marginBottom: '16px' }}>
              <div className="flex justify-between items-center">
                <Title level={4}>Dados Pessoais Tratados</Title>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addDado}
                >
                  Adicionar Dado
                </Button>
              </div>
            </div>

            <Form.List name="dados">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Card
                      key={key}
                      size="small"
                      title={`Dado #${index + 1}`}
                      extra={
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      }
                      style={{ marginBottom: '16px' }}
                    >
                      <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            {...restField}
                            label="Tipo de Dado"
                            name={[name, 'dado_id']}
                            rules={[{ required: true, message: 'Selecione um tipo de dado' }]}
                          >
                            <Select placeholder="Selecione um dado">
                              {dados.map(d => (
                                <Option key={d._id} value={d._id}>
                                  {d.nome} ({d.tipo})
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item
                            {...restField}
                            label="Origem"
                            name={[name, 'origem']}
                            rules={[{ required: true, message: 'Selecione a origem' }]}
                          >
                            <Select>
                              {Object.values(OrigemDados).map(origem => (
                                <Option key={origem} value={origem}>
                                  {origem}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item
                            {...restField}
                            label="Consentimento do Titular"
                            name={[name, 'consentimento_titular']}
                            rules={[{ required: true, message: 'Selecione o consentimento' }]}
                          >
                            <Select>
                              {Object.values(ConsentimentoTitular).map(consentimento => (
                                <Option key={consentimento} value={consentimento}>
                                  {consentimento}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item
                            {...restField}
                            label="Frequência de Coleta"
                            name={[name, 'frequencia']}
                            rules={[{ required: true, message: 'Selecione a frequência' }]}
                          >
                            <Select>
                              {Object.values(FrequenciaColeta).map(freq => (
                                <Option key={freq} value={freq}>
                                  {freq}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24}>
                          <Form.Item
                            {...restField}
                            label="Motivo do Tratamento"
                            name={[name, 'motivo']}
                          >
                            <TextArea
                              rows={2}
                              placeholder="Descreva o motivo do tratamento deste dado..."
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </>
              )}
            </Form.List>
          </TabPane>

          <TabPane tab="Observações" key="observacoes">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Observações - Coleta"
                  name="obs_coleta"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Observações sobre a coleta de dados..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Observações - Processamento"
                  name="obs_processamento"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Observações sobre o processamento de dados..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Observações - Compartilhamento"
                  name="obs_compartilhamento"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Observações sobre o compartilhamento de dados..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Observações - Armazenamento"
                  name="obs_armazenamento"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Observações sobre o armazenamento de dados..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Finalidades" key="finalidades">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Finalidade - Coleta"
                  name="finalidade_coleta"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Descreva a finalidade da coleta de dados..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Finalidade - Processamento"
                  name="finalidade_processamento"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Descreva a finalidade do processamento de dados..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Finalidade - Compartilhamento"
                  name="finalidade_compartilhamento"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Descreva a finalidade do compartilhamento de dados..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Finalidade - Armazenamento"
                  name="finalidade_armazenamento"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Descreva a finalidade do armazenamento de dados..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Medidas de Segurança" key="medidas">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Medidas de Segurança - Coleta"
                  name="medidas_coleta"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Descreva as medidas de segurança para coleta..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Medidas de Segurança - Processamento"
                  name="medidas_processamento"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Descreva as medidas de segurança para processamento..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Medidas de Segurança - Compartilhamento"
                  name="medidas_compartilhamento"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Descreva as medidas de segurança para compartilhamento..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="Medidas de Segurança - Armazenamento"
                  name="medidas_armazenamento"
                  rules={[
                    { max: 5000, message: 'Máximo de 5000 caracteres' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Descreva as medidas de segurança para armazenamento..."
                    showCount
                    maxLength={5000}
                  />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
        </Tabs>

        <Divider />

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>
              Cancelar
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting || loading}
              icon={<SaveOutlined />}
            >
              Salvar
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default TitularForm;